/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertColor, Box, IconButton, Select, SelectChangeEvent, Snackbar, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';

import {
  determineScheduleDate,
  getDateRange,
  getFormattedDates,
  getStoredCustomerDetails,
  useDateRangeOptions,
} from '../../../../../Shared/Utilities/utils';
import { NumberOfRowsOptions } from '../../../../../Shared/Constants/App';
import Modal from '../../../../Jobs/Utilities/Shared/Components/Modal';

import { CancellationOptions } from '../AllSchedules.Model';
import { JobManagementType } from '../../AllJobs/AllJobs.Model';
import { DateRangeSelector } from '../../../../../Shared/Components/Common/DateRangeSelection/DateRangeSelection.Model';
import WorkOrdersApiService from '../../../../Jobs/Services/WorkOrdersService';

import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import DataGrid from '../../../../../Shared/Components/Common/DataGrid/DataGrid';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import ModifyPopUp from '../../../../Jobs/Components/JobDetails/ModifyPopUp';
import RenderCancelShiftBodyContent from './RenderCancellationOptions';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import DateRangeSelection from '../../../../../Shared/Components/Common/DateRangeSelection/DateRangeSelection';
import ScheduleColumns from './ScheduleColumns';
import { Labels } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer';

import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import { SnackBarWrapper } from '../../../../Jobs/Components/JobDetailsOverView/JobDetailsOverview.styles';
import { DropdownWrapper } from '../../../../Jobs/Components/WorkTasks/JobDetailsTasks.styles';
import { ActionsColumn } from '../../../../../Shared/Components/Common/DataGrid/DataGrid.styles';
import WhiteTooltip from '../../../../../Shared/Components/Common/CommonStyle/Tooltip.Style';
import { HeadingText } from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails.Style';
import { PaginationStyle } from '../AllJobs.Style';
import { StyledPagination } from '../../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import { DropDown, SelectInput } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';
import { Options } from '../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import theme from '../../../../../Shared/Themes/theme';
import { Icons } from '../../../../../Shared/Constants/Icons';

interface ShiftData {
  lastDateOfService: string;
  closureStartDate: string;
  shiftRestartDate: string;
  shiftCancellationDate: string;
  type: string;
}

const CancelShift = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCancelShiftLoading, setIsCancelShiftLoading] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const [selectedMultipleShifts, setIsSelectedMultipleShifts] = useState(false);
  const [isSelectOptionsOpen, setIsSelectOptionsOpen] = useState(false);
  const [isConfirmPopUpOpen, setIsConfirmPopUp] = useState(false);

  const [shiftData, setShiftData] = useState<ShiftData>();
  const [woScheduleMasterId, setWoScheduleMasterId] = useState<string>();

  const [selectedShiftData, setSelectedShiftData] = useState<any>({});
  const [selectedMultipleShiftsData, setSelectedMultipleShiftsData] = useState<any>([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');

  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeSelector>({
    fromDate: null,
    toDate: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isValueOpen, setValueOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [fromDate, setFromDate] = useState<string>();
  const [toDate, setToDate] = useState<string>();

  const dateRangeOptions = useDateRangeOptions();

  const defaultDateRangeOption = '7';

  const toggleValue = () => {
    setValueOpen(!isValueOpen);
  };

  const { t } = useTranslation([
    'jobManagement',
    'messages',
    'noData',
    'tableHeader',
    'calendar',
    'taskRejectionAndRework',
  ]);

  const location = useLocation();

  const { control } = useForm({
    defaultValues: {
      specificDate: new Date().toISOString(),
    },
  });

  const specificDate = useWatch({
    control,
    name: 'specificDate',
  });

  const fetchSchedulesData = useCallback(
    async (pageNumber: number, limit: number, woScheduleMasterId?: string, fromDate?: string, toDate?: string) => {
      setIsLoading(true);
      try {
        const { fromDateYMDFormat, toDateYMDFormat } = getFormattedDates(fromDate, toDate);

        if (!isEmpty(fromDateYMDFormat) || !isEmpty(toDateYMDFormat)) {
          const scheduleDate = determineScheduleDate(fromDateYMDFormat, toDateYMDFormat);

          const schedulesByDateResponse = await WorkOrdersApiService.schedulesByDateV2(
            scheduleDate ?? '',
            '',
            false,
            '',
            woScheduleMasterId,
            !scheduleDate ? fromDateYMDFormat : '',
            !scheduleDate ? toDateYMDFormat : '',
            limit,
            pageNumber
          );

          setSchedules(schedulesByDateResponse?.data);
          setTotalCount(schedulesByDateResponse?.metaData?.totalCount);
        }
      } catch (error) {
        console.error(t('messages:errorFetchingJobs'), error);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedDateRange, woScheduleMasterId]
  );

  const renderActionsCell = (row: any) => {
    const { dataStatus } = row.original;
    const isEditable = dataStatus === 'A';
    return (
      <ActionsColumn>
        <IconButton
          size="large"
          sx={{
            '&:hover': {
              background: isEditable ? theme.palette.primary.main : theme.palette.common.white,
            },
          }}
          onClick={() => {
            if (isEditable) {
              setIsSelectOptionsOpen(true);
              setSelectedShiftData(row.original);
              setSelectedMultipleShiftsData([]);
            }
          }}>
          <WhiteTooltip title={t('jobManagement:cancelShift')}>
            <img
              src={Icons.CancelIcon}
              alt={t('jobManagement:cancelShift')}
              style={{ cursor: isEditable ? 'pointer' : 'not-allowed', opacity: isEditable ? 1 : 0.3 }}
            />
          </WhiteTooltip>
        </IconButton>
      </ActionsColumn>
    );
  };

  const columns = ScheduleColumns({
    renderActionsCell: renderActionsCell,
  });

  const handleCloseCancelShift = () => {
    setIsSelectedMultipleShifts(!selectedMultipleShifts);
  };

  const handleConfirmPopup = () => {
    setIsConfirmPopUp(!isConfirmPopUpOpen);
  };

  const handleContinueButton = () => {
    setIsSelectOptionsOpen(true);
  };

  const cancelShiftsPayload = (selectedShiftsData: any[], shiftData: ShiftData | undefined) => {
    const result: any[] = [];

    selectedShiftsData.forEach((schedule) => {
      const { workOrderId, workOrderScheduleId, woShiftId, empShiftId, woScheduleMasterId } = schedule;
      let workOrder = result.find((item) => item.workOrderId === workOrderId);

      if (!workOrder) {
        workOrder = {
          workOrderId,
          cancelWorkOrderScheduleInput: [],
        };
        result.push(workOrder);
      }

      if (shiftData?.type === CancellationOptions.PermanentClosure && shiftData?.lastDateOfService) {
        const originalDate = new Date(shiftData.lastDateOfService);

        const updatedDate = new Date(
          Date.UTC(originalDate.getUTCFullYear(), originalDate.getUTCMonth(), originalDate.getUTCDate())
        );

        const lastDateOfService = updatedDate;
        workOrder.cancelWorkOrderScheduleInput.push({
          type: shiftData?.type,
          lastDateOfService: lastDateOfService ?? '',
          workOrderScheduleId,
          woShiftId,
          empShiftId,
          woScheduleMasterId,
        });
      }

      if (
        shiftData?.type === CancellationOptions.TemporaryClosure &&
        shiftData?.closureStartDate &&
        shiftData?.shiftRestartDate
      ) {
        const originalClosureStartDate = new Date(shiftData.closureStartDate);
        const originalShiftRestartDate = new Date(shiftData.shiftRestartDate);

        const updatedClosureStartDate = new Date(
          Date.UTC(
            originalClosureStartDate.getUTCFullYear(),
            originalClosureStartDate.getUTCMonth(),
            originalClosureStartDate.getUTCDate()
          )
        );

        const updatedShiftRestartDate = new Date(
          Date.UTC(
            originalShiftRestartDate.getUTCFullYear(),
            originalShiftRestartDate.getUTCMonth(),
            originalShiftRestartDate.getUTCDate()
          )
        );

        workOrder.cancelWorkOrderScheduleInput.push({
          type: shiftData?.type,
          closureStartDate: updatedClosureStartDate ?? '',
          shiftRestartDate: updatedShiftRestartDate ?? '',
          workOrderScheduleId,
          woShiftId,
          empShiftId,
          woScheduleMasterId,
        });
      }

      if (shiftData?.type === CancellationOptions.ShiftCancellation) {
        workOrder.cancelWorkOrderScheduleInput.push({
          type: shiftData?.type,
          workOrderScheduleId,
          woShiftId,
          empShiftId,
          woScheduleMasterId,
        });
      }
    });

    return result;
  };

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleConfirmButton = async () => {
    try {
      setIsCancelShiftLoading(true);
      const { userId } = getStoredCustomerDetails() || {};

      let payload;
      if (!isEmpty(selectedMultipleShiftsData)) {
        payload = cancelShiftsPayload(selectedMultipleShiftsData, shiftData);
      } else {
        payload = cancelShiftsPayload([selectedShiftData], shiftData);
      }
      const cancelSchedulesResponse = await WorkOrdersApiService.getCancelSchedules(payload, userId);

      if (cancelSchedulesResponse?.data) {
        const allFailedEmpty = cancelSchedulesResponse?.data?.every(
          (schedule: { cancelWorkOrderSchedules: { failed: string | any[] } }) =>
            schedule.cancelWorkOrderSchedules.failed.length === 0
        );

        const message = allFailedEmpty
          ? isEmpty(selectedMultipleShiftsData)
            ? 'jobManagement:shiftCancelledSuccessfully'
            : 'jobManagement:shiftsCancelledSuccessfully'
          : isEmpty(selectedMultipleShiftsData)
            ? 'jobManagement:shiftCancellationFailed'
            : 'jobManagement:shiftsCancellationFailed';

        const messageType = allFailedEmpty ? 'success' : 'error';
        showSnackbar(messageType, t(message));
      } else {
        showSnackbar('error', cancelSchedulesResponse?.errors?.[0]?.message);
      }
      setIsCancelShiftLoading(false);
      setIsConfirmPopUp(false);
      setIsSelectOptionsOpen(false);
      setIsSelectedMultipleShifts(false);
      fetchSchedulesData(currentPage, itemsPerPage, woScheduleMasterId as string, fromDate as string, toDate);
    } catch (error) {
      console.error(t('jobManagement:cancelShiftError'), error);
      showSnackbar('error', t('jobManagement:cancelShiftError'));
    }
  };

  useEffect(() => {
    const fromDate = selectedDateRange?.fromDate ?? '';
    const toDate = selectedDateRange?.toDate ?? '';
    const woScheduleMasterId: string = location.state?.selectedRows
      ?.map((row: { woScheduleMasterId: string }) => row.woScheduleMasterId)
      ?.join(',');
    setFromDate(fromDate as string);
    setToDate(toDate as string);
    setWoScheduleMasterId(woScheduleMasterId);
    fetchSchedulesData(currentPage, itemsPerPage, woScheduleMasterId, fromDate as string, toDate as string);
  }, [selectedDateRange]);

  useEffect(() => {
    const { fromDate, toDate } = getDateRange(7);
    setSelectedDateRange({
      fromDate: fromDate,
      toDate: toDate,
    });
  }, []);

  const renderCancelShiftHeaderContent = () => {
    return <HeadingText sx={{ fontSize: theme.typography.h2?.fontSize }}>{t('jobManagement:cancelShift')}</HeadingText>;
  };

  const handleOnConfirm = (data: any) => {
    setIsConfirmPopUp(true);
    setShiftData(data);
  };

  const renderCancelShiftBodyContent = () => {
    return (
      <>
        <RenderCancelShiftBodyContent handleOnConfirm={handleOnConfirm} selectedDate={specificDate} />
      </>
    );
  };

  const handleDateRangeChange = (dateRange: DateRangeSelector) => {
    setSelectedDateRange({
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    });
  };

  const handleChange = (event: any, value: number): void => {
    setCurrentPage(value);
    fetchSchedulesData(value, itemsPerPage, woScheduleMasterId, fromDate, toDate);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newItemsPerPage = Number(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchSchedulesData(currentPage, newItemsPerPage, woScheduleMasterId, fromDate, toDate);
  };

  return (
    <>
      <PageTitle title={t('jobManagement:cancelShift')} />
      <CustomerContainer>
        <DropdownWrapper sx={{ justifyContent: 'flex-end', marginTop: '2rem', marginBottom: '2rem' }}>
          <DateRangeSelection
            onDateRangeChange={handleDateRangeChange}
            defaultOption={defaultDateRangeOption}
            options={dateRangeOptions}
            isExcludePastDate={false}
            isRescheduleShift={true}
            showSpecificRangeDates={{
              from: new Date('2023-01-01'),
              to: dayjs().endOf('month').toDate(),
            }}
          />
        </DropdownWrapper>
        <DataGrid
          columns={columns}
          data={schedules}
          enableRowSelect={true}
          enableColumnPinning={false}
          loader={isLoading}
          errorMessageTitle={t('noData:noDataYet')}
          errorMessageDescription={t('noData:comeBackLater')}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
          type={JobManagementType.AllSchedules}
          isRescheduleShift={true}
        />
        {!isLoading && totalCount > 10 && (
          <PaginationStyle>
            <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
              <StyledPagination
                count={Math.ceil(totalCount / itemsPerPage)}
                page={currentPage}
                onChange={(event, value) => {
                  handleChange(event, value);
                }}
                color="primary"
              />
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3rem' }}>
              <Typography>
                {t('dashboard:totalItems')}: {totalCount}
              </Typography>
              {totalCount > 10 && (
                <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <Typography>{t('dashboard:recordsPerPage')}:</Typography>
                  <DropDown>
                    <Box onClick={toggleValue}>
                      <Select
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        label="Records"
                        input={<SelectInput />}
                        IconComponent={() => (
                          <img
                            src={Icons.DropdownIcon}
                            style={{
                              transform: isValueOpen ? Labels.Rotate : 'none',
                              position: 'absolute',
                              right: '0.75rem',
                            }}
                          />
                        )}
                        open={isValueOpen}
                        onClose={toggleValue}
                        onOpen={toggleValue}>
                        {NumberOfRowsOptions.map((dataItem) => (
                          <Options key={dataItem.id} value={dataItem.value}>
                            {dataItem.label}
                          </Options>
                        ))}
                      </Select>
                    </Box>
                  </DropDown>
                </Box>
              )}
            </Box>
          </PaginationStyle>
        )}
        {selectedRows?.length > 0 && (
          <SnackBarWrapper>
            <Snackbar
              open
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              action={
                <Button
                  type={ButtonType.Primary}
                  label={t('jobManagement:cancelShift')}
                  onClick={() => {
                    if (selectedRows?.length <= 1) {
                      setIsSelectOptionsOpen(true);
                      setSelectedShiftData({});
                      setSelectedMultipleShiftsData(selectedRows);
                    } else {
                      setIsSelectedMultipleShifts(true);
                      setSelectedShiftData({});
                      setSelectedMultipleShiftsData(selectedRows);
                    }
                  }}
                />
              }
            />
          </SnackBarWrapper>
        )}

        <ModifyPopUp
          open={selectedMultipleShifts}
          handleClose={handleCloseCancelShift}
          handleOnCloseJob={handleContinueButton}
          heading={t('jobManagement:cancelShift')}
          label={t('jobManagement:continue')}
          text={
            <>
              {t('jobManagement:shiftsSelected')} <strong>{selectedRows?.length}</strong> {t('jobManagement:shifts')}{' '}
              {t('jobManagement:confirmCancelShiftText')}
            </>
          }
          width={'41.25rem'}
        />
        <ModifyPopUp
          open={isConfirmPopUpOpen}
          handleClose={handleConfirmPopup}
          handleOnCloseJob={handleConfirmButton}
          heading={t('jobManagement:cancelShift')}
          text={t('jobManagement:cancelShiftPopupText')}
          loading={isCancelShiftLoading}
        />
        <Modal
          open={isSelectOptionsOpen}
          onClose={() => {
            setIsSelectOptionsOpen(false);
            setIsSelectedMultipleShifts(false);
          }}
          renderHeader={renderCancelShiftHeaderContent()}
          renderBody={renderCancelShiftBodyContent()}
        />
        <SnackbarMessage
          open={snackbarOpen}
          successMessage={snackbarMessage}
          errorMessage={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </CustomerContainer>
    </>
  );
};

export default CancelShift;
